/**
 * InlineUploadPanel — Iter 290r
 *
 * The unified Upload / Scan surface for sales documents (quote, sales
 * order, invoice, credit note, statement). Lives inline on the customer
 * Documents tab — NO modal / no navigation away.
 *
 * Contract:
 *   1. Audience picker (DNU / CF) is the FIRST UI element. For quotes
 *      and sales_orders it is HARD-REQUIRED — file actions are disabled
 *      until a choice is made. For invoice / credit_note / statement
 *      it is auto-locked to "customer".
 *   2. Once an audience is chosen, the user can either:
 *        a. drag-drop a file
 *        b. tap "Choose file" (PC file picker)
 *        c. tap "Scan" (mobile camera capture)
 *   3. The file is uploaded immediately to `/sales-documents/bulk-upload`
 *      with the audience baked in. OCR runs as a background task.
 *   4. On success the panel closes, the parent reloads, and the new row
 *      appears with a "Scanning…" badge while OCR fills in line items.
 *
 * NO sync OCR preview. NO "Apply suggestion" step. The PDF lands in
 * storage in 1–2 seconds.
 */
import { useRef, useState } from "react";
import { Upload as UploadIcon, Camera, Loader2, X, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";

// Iter 290zp.15z05 — Invoices now support DNU + CF (matches backend
// iter 290zp.15z04 where the audience-coercion was lifted). Audience
// picker must appear so the rep explicitly tags every invoice before
// upload. credit_note + statement remain customer-only (refund /
// balance docs — no DNU variant makes commercial sense).
const ALLOW_AUDIENCE_PICK = new Set(["quote", "sales_order", "invoice"]);
const FORCED_CUSTOMER_CATS = new Set(["credit_note", "statement"]);

export default function InlineUploadPanel({
  customerId,
  category,           // 'quote' | 'sales_order' | 'invoice' | 'credit_note' | 'statement'
  defaultCapture = false,  // true = mobile camera (Scan button), false = PC (Upload button)
  onClose,
  onUploaded,
}) {
  const lockedCustomer = FORCED_CUSTOMER_CATS.has(category);
  const [audience, setAudience] = useState(lockedCustomer ? "customer" : "");
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const canUpload = !!audience && !busy;
  const requiresAudience = ALLOW_AUDIENCE_PICK.has(category);

  const upload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    if (requiresAudience && !audience) {
      toast.error("Pick DNU or CF first — every quote must be tagged.");
      return;
    }
    setBusy(true);
    setPct(0);
    try {
      const fd = new FormData();
      fd.append("customer_id", customerId);
      fd.append("category", category);
      fd.append("audience", audience || "customer");
      list.forEach((f) => fd.append("files", f, f.name));
      const { data } = await api.post("/sales-documents/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600_000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setPct(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
        },
      });
      setPct(100);
      const n = data?.count || 0;
      const label = (audience === "internal" ? "DNU " : audience === "customer" ? "CF " : "") + category.replace("_", " ");
      toast.success(
        n === 1
          ? `1 ${label} uploaded — OCR running in background.`
          : `${n} ${label}s uploaded — OCR running in background.`,
      );
      onUploaded?.(data?.created || []);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUpload) {
      toast.error("Pick DNU or CF first.");
      return;
    }
    const files = e.dataTransfer?.files;
    if (files?.length) upload(files);
  };

  const categoryLabel = category.replace("_", " ");

  return (
    <div
      className="hv-card !p-3 space-y-3 border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.03]"
      data-testid="inline-upload-panel"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold tracking-[0.18em] uppercase text-white flex items-center gap-1.5">
          <UploadIcon className="w-3.5 h-3.5 text-[#1FB6C1]" />
          {defaultCapture ? "Scan" : "Upload"} {categoryLabel}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-[#647087] hover:text-white"
            data-testid="inline-upload-close"
            aria-label="Close upload panel"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </header>

      {/* Audience picker — STEP 1, hard-required for quote / sales_order */}
      {requiresAudience && (
        <div
          className="rounded-md border border-amber-400/30 bg-amber-400/[0.04] p-3 space-y-2"
          data-testid="inline-upload-audience-block"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300 flex items-center gap-1.5">
            <ShieldAlert className="w-3 h-3" />
            Step 1 · who is this for?
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setAudience("internal")}
              disabled={busy}
              data-testid="inline-upload-audience-internal"
              aria-pressed={audience === "internal"}
              className={`text-left p-3 rounded-md border transition disabled:opacity-50 ${
                audience === "internal"
                  ? "border-rose-300 bg-rose-500/15 shadow-[0_0_0_2px_rgba(244,63,94,.25)]"
                  : "border-white/10 hover:border-rose-400/50 bg-white/[0.02]"
              }`}
            >
              <div className="text-xs font-bold text-rose-300">🔴 Internal · DNU</div>
              <div className="text-[10px] text-[#a0aabf] mt-0.5 leading-snug">
                Engineering doc · NEVER shown to customer.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setAudience("customer")}
              disabled={busy}
              data-testid="inline-upload-audience-customer"
              aria-pressed={audience === "customer"}
              className={`text-left p-3 rounded-md border transition disabled:opacity-50 ${
                audience === "customer"
                  ? "border-emerald-300 bg-emerald-500/15 shadow-[0_0_0_2px_rgba(16,185,129,.25)]"
                  : "border-white/10 hover:border-emerald-400/50 bg-white/[0.02]"
              }`}
            >
              <div className="text-xs font-bold text-emerald-300">🟢 Customer · CF</div>
              <div className="text-[10px] text-[#a0aabf] mt-0.5 leading-snug">
                Safe to share via portal. Customers see this.
              </div>
            </button>
          </div>
        </div>
      )}

      {/* File input — STEP 2, blocked until audience picked */}
      <label
        htmlFor={`inline-upload-input-${category}`}
        onDrop={onDrop}
        onDragOver={(e) => { if (canUpload) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        className={`block border-2 border-dashed rounded-md p-5 text-center transition cursor-pointer ${
          !canUpload
            ? "border-white/5 bg-white/[0.01] cursor-not-allowed opacity-50"
            : dragOver
              ? "border-[#1FB6C1] bg-[#1FB6C1]/10"
              : "border-white/15 hover:border-[#1FB6C1]/60 bg-white/[0.02]"
        }`}
        data-testid="inline-upload-dropzone"
      >
        <input
          id={`inline-upload-input-${category}`}
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          disabled={!canUpload}
          className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
          data-testid="inline-upload-file-input"
        />
        {/* Hidden camera input — only used by the Scan button below */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          disabled={!canUpload}
          className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
          data-testid="inline-upload-camera-input"
        />

        {!canUpload && requiresAudience ? (
          <>
            <ShieldAlert className="w-6 h-6 text-amber-300/60 mx-auto mb-1" />
            <div className="text-xs text-amber-200/80">
              Pick DNU or CF above to enable upload
            </div>
          </>
        ) : busy ? (
          <>
            <Loader2 className="w-6 h-6 text-[#1FB6C1] mx-auto mb-2 animate-spin" />
            <div className="text-xs text-[#1FB6C1] font-semibold">
              {pct < 100 ? `Uploading… ${pct}%` : "Upload complete — OCR queued."}
            </div>
            <div className="h-1 w-full bg-white/5 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-[#1FB6C1] transition-all" style={{ width: `${pct}%` }} />
            </div>
          </>
        ) : (
          <>
            <UploadIcon className="w-6 h-6 text-[#647087] mx-auto mb-1" />
            <div className="text-xs text-white font-semibold">
              Drop PDF or image to upload
            </div>
            <div className="text-[10px] text-[#647087] mt-0.5">
              OCR runs in the background · multi-file supported
            </div>
            <div className="flex items-center justify-center gap-2 mt-3">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}
                disabled={!canUpload}
                className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                data-testid="inline-upload-choose-file"
              >
                <UploadIcon className="w-3 h-3" /> Choose file
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); cameraInputRef.current?.click(); }}
                disabled={!canUpload}
                className="hv-btn hv-btn-ghost text-xs disabled:opacity-50"
                data-testid="inline-upload-camera"
              >
                <Camera className="w-3 h-3" /> Scan (camera)
              </button>
            </div>
          </>
        )}
      </label>
    </div>
  );
}
